import { FC, useContext } from "react";

import { FormType } from "@simplyk/common";

import { FrontendTicketingContext } from "../../../contexts/FrontendTicketingContext";
import { useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationProvider } from "../context/heartsAnimationContext";

import { TicketingV2CheckoutFooter } from "./CheckoutFooter/TicketingV2CheckoutFooter";
import { OccurrenceSelectorProvider } from "./OccurenceSelector/OccurenceSelectorContext";
import { TicketingV2OccurrenceSelector } from "./OccurenceSelector/TicketingV2OccurrenceSelector";
import { TicketingSelectionV2 } from "./TicketingSelectionV2";
import { TicketingV2AdditionalDonation } from "./TicketingV2AdditionalDonation";

import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
import { useEnterKeyListener } from "@/features/FormV2/hooks/useEnterKeyListener";
import { useThemedFormZeffyLogoUrl } from "@/features/FormV2/hooks/useThemedFormZeffyLogoUrl";
import { useUtmForm } from "@/features/FormV2/hooks/useUtmForm";
import { useTicketingFirstStepSubmit } from "@/features/FormV2/Step1/CheckoutFooter/useTicketingFirstStepSubmit";
import { FormV2GenerateReceiptInfobox } from "@/features/FormV2/Step1/FormV2GenerateReceiptInfobox";
import { FormV2LogoChip } from "@/features/FormV2/Step1/FormV2LogoChip";
import { FormV2ZeffyBusinessModelInfobox } from "@/features/FormV2/Step1/FormV2ZeffyBusinessModelInfobox";
import { MobileFormV2Banner } from "@/features/FormV2/Step1/MobileFormV2Banner";
import { TicketingFormV2Summary } from "@/features/FormV2/Step1/Summary/TicketingFormV2Summary";
import { TicketingV2Step1Layout } from "@/features/FormV2/Step1/TicketingV2Step1Layout";
import { useTrackThemedFormLogoClicked } from "@/features/FormV2/tracking/useTrackThemedFormLogoClicked";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";

export type TicketingV2Step1Props = {
  isEmbed?: boolean;
};

export const TicketingV2Step1: FC<TicketingV2Step1Props> = ({ isEmbed = false }) => {
  const { hasBanner, description, formObject, currentStep, formCategory } = useFormV2Context(FormType.Ticketing);
  const { ticketingIsClosed } = useContext(FrontendTicketingContext);
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const { onFirstStepSubmit, canSubmitFirstStep, totalTickets, additionalDonation } = useTicketingFirstStepSubmit();
  const { isPreview } = usePreviewContext();
  const utm = useUtmForm();

  const onTrackLogoClick = useTrackThemedFormLogoClicked({
    formType: FormType.Ticketing,
    formCategory,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const isOneColumnLayout = !hasBanner && !description;

  useEnterKeyListener(onFirstStepSubmit, currentStep === "selection");

  return (
    <OccurrenceSelectorProvider>
      <HeartsAnimationProvider>
        <TicketingV2Step1Layout
          contextDrawerHeight={isExpanded ? bannerHeight : 0}
          additionalDonationPosition={ticketingIsClosed ? "top-of-page" : "default"}
          isEmbed={isEmbed}
          isOneColumn={isOneColumnLayout}
          slots={{
            banner: hasBanner ? <MobileFormV2Banner /> : undefined,
            organizationLogo: (
              <FormV2LogoChip organizationName={formObject?.organization?.name} logoUrl={formObject?.logoUrl} />
            ),
            summary: <TicketingFormV2Summary />,
            occurrenceSelector: <TicketingV2OccurrenceSelector />,
            additionalDonation: <TicketingV2AdditionalDonation />,
            rates: <TicketingSelectionV2 />,
            generateReceipt: <FormV2GenerateReceiptInfobox />,
            zeffyBusinessModel: <FormV2ZeffyBusinessModelInfobox />,
            checkoutFooter: (
              <TicketingV2CheckoutFooter hasDonation={additionalDonation > 0} totalTickets={totalTickets} />
            ),
          }}
          showCheckoutFooter={canSubmitFirstStep && !isPreview}
          logoHref={logoHref}
          onLogoClick={onTrackLogoClick}
        />
      </HeartsAnimationProvider>
    </OccurrenceSelectorProvider>
  );
};
